import React, { useEffect, useState } from 'react';
import axios from 'axios';

// E-mail ao COLABORADOR com as orientações do exame. Dois modos:
//  - periodico (Exame Mensal): clínica, endereço, horário, o kit em anexo e o pedido de devolver o ASO;
//  - admissional (checklist da admissão): depois do agendamento, data, horário, clínica e
//    endereço (puxados do pedido à clínica / último envio, ou digitados) + a guia em anexo.
// O texto é livre; ao enviar (ou em "Salvar como modelo") ele vira o modelo da empresa,
// então o endereço e o horário da clínica só são digitados uma vez. O Enviar fica na prévia.

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const URL_AVISO = () => `${API}/api/bporh/exames/aviso-colaborador/`;

// "14/10/2026" ⇄ "2026-10-14"
const paraIso = (br) => {
  const m = String(br || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
};
const paraBr = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : '';
};
const ORIGEM = { corporativo: 'e-mail corporativo', alternativo: 'e-mail alternativo' };
const render = (t, ctx) => String(t || '').replace(/\{(\w+)\}/g, (m, k) => (k in ctx ? ctx[k] : m));
// campos do modo admissional (além da data) que vão para o texto
const CAMPOS_ADM = [['horario', 'Horário', 'ex.: 08:30'], ['clinica', 'Clínica', 'nome da clínica'],
  ['endereco', 'Endereço', 'endereço da clínica']];

const AvisoExameColaborador = ({ clienteId, colaborador, tipo = 'periodico', onFechar, onEnviado, onErro, onAviso }) => {
  const adm = tipo === 'admissional';
  const [form, setForm] = useState(null);        // {para, assunto, corpo, data, operador_id, anexos[], horario, clinica, endereco, anexar_guia}
  const [info, setInfo] = useState(null);        // resposta do GET (remetentes, histórico, origem, modelo)
  const [previa, setPrevia] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  // admissional: enquanto o texto não for editado à mão, ele é remontado do modelo a cada campo
  const [sujo, setSujo] = useState(false);

  const carregar = () => {
    const dataInicial = paraIso(colaborador.agendado_para) || paraIso(colaborador.solicitacao?.data_exame);
    axios.get(URL_AVISO(), {
      params: { cliente_id: clienteId, convenia_id: colaborador.convenia_id, data: dataInicial, tipo },
      headers: headers() })
      .then((r) => {
        setInfo(r.data);
        setSujo(false);
        const ctx = r.data.ctx || {};
        setForm({ para: r.data.para || '', assunto: r.data.assunto, corpo: r.data.corpo,
          data: adm ? (r.data.data_iso || dataInicial) : dataInicial,
          operador_id: r.data.operador_padrao ? String(r.data.operador_padrao) : '', anexos: [],
          horario: ctx.horario || '', clinica: ctx.clinica || '', endereco: ctx.endereco || '', anexar_guia: true });
      })
      .catch((e) => { onErro(e.response?.data?.mensagem || 'Não foi possível abrir o e-mail'); onFechar(); });
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(carregar, [clienteId, colaborador.convenia_id, tipo]);

  const mudar = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));
  const editarTexto = (campo, valor) => { if (adm) setSujo(true); mudar(campo, valor); };

  // admissional: data/horário/clínica/endereço entram no texto — remonta do modelo
  // (texto intocado) ou troca o valor antigo pelo novo (texto já editado à mão)
  const mudarCampoAdm = (campo, valor) => setForm((f) => {
    const novo = { ...f, [campo]: valor };
    const ctxDe = (x) => ({ ...(info?.ctx || {}), data_atendimento: paraBr(x.data), horario: x.horario,
      clinica: x.clinica, endereco: x.endereco });
    if (!sujo && info?.modelo) {
      return { ...novo, assunto: render(info.modelo.assunto, ctxDe(novo)), corpo: render(info.modelo.corpo, ctxDe(novo)) };
    }
    const chave = campo === 'data' ? 'data_atendimento' : campo;
    const antes = ctxDe(f)[chave];
    const depois = ctxDe(novo)[chave];
    if (!antes) return novo;
    const troca = (t) => t.split(antes).join(depois);
    return { ...novo, assunto: troca(f.assunto), corpo: troca(f.corpo) };
  });

  // periódico: trocar a data troca também a data já escrita no assunto e no texto
  const mudarData = (iso) => {
    if (adm) { mudarCampoAdm('data', iso); return; }
    setForm((f) => {
      const antes = paraBr(f.data);
      const depois = paraBr(iso) || '{data_atendimento}';
      const troca = (t) => (antes ? t.split(antes).join(depois)
        : t.split('{data_atendimento}').join(paraBr(iso) || '{data_atendimento}'));
      return { ...f, data: iso, assunto: troca(f.assunto), corpo: troca(f.corpo) };
    });
  };
  const juntarAnexos = (lista) => setForm((f) => ({
    ...f, anexos: [...f.anexos, ...Array.from(lista || []).filter(n => !f.anexos.some(a => a.name === n.name))] }));

  const corpoBase = (acao) => ({
    acao, tipo, cliente_id: clienteId, convenia_id: colaborador.convenia_id, operador_id: form.operador_id || '',
    para: form.para, assunto: form.assunto, corpo: form.corpo, data_atendimento: form.data || '',
    ...(adm ? { horario: form.horario, clinica: form.clinica, endereco: form.endereco,
      anexar_guia: form.anexar_guia ? '1' : '0' } : {}),
  });

  const verGuia = () => {
    axios.get(`${API}/api/bporh/exames/solicitar/`, {
      params: { cliente_id: clienteId, convenia_id: colaborador.convenia_id, pdf: 1, tipo: 'admissional',
        data_exame: form.data || undefined },
      headers: headers(), responseType: 'blob' })
      .then((r) => window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank'))
      .catch(() => onErro('Não foi possível gerar a guia'));
  };

  const verPrevia = () => {
    setOcupado(true);
    axios.post(URL_AVISO(), { ...corpoBase('previa'), anexos_nomes: form.anexos.map(a => a.name) },
      { headers: headers() })
      .then((r) => setPrevia(r.data))
      .catch((e) => onErro(e.response?.data?.mensagem || 'Não foi possível montar a prévia'))
      .finally(() => setOcupado(false));
  };

  const enviar = () => {
    const fd = new FormData();
    Object.entries(corpoBase('enviar')).forEach(([k, v]) => fd.append(k, v ?? ''));
    form.anexos.forEach((a) => fd.append('anexos', a));
    setOcupado(true);
    axios.post(URL_AVISO(), fd, { headers: headers(), timeout: 120000 })
      .then((r) => onEnviado(r.data.mensagem))
      .catch((e) => onErro(e.response?.data?.mensagem || 'Não foi possível enviar'))
      .finally(() => setOcupado(false));
  };

  const salvarModelo = () => {
    setOcupado(true);
    axios.post(URL_AVISO(), corpoBase('salvar_modelo'), { headers: headers() })
      .then((r) => { setInfo((i) => ({ ...i, modelo_proprio: true })); onAviso?.(r.data.mensagem); })
      .catch((e) => onErro(e.response?.data?.mensagem || 'Não foi possível salvar o modelo'))
      .finally(() => setOcupado(false));
  };

  const restaurar = () => {
    axios.post(URL_AVISO(), { acao: 'restaurar', cliente_id: clienteId, tipo }, { headers: headers() })
      .then(() => carregar())
      .catch((e) => onErro(e.response?.data?.mensagem || 'Não foi possível restaurar'));
  };

  const bloqueiaEnvio = previa && (previa.avisos || []).some(a => !a.startsWith('Linha(s)') && !a.startsWith('Nenhum anexo'));

  return (
    <>
      <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10000 }}>
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content shadow-lg">
            <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
              <div>
                <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                  <i className="fas fa-paper-plane me-2"></i>
                  {adm ? 'E-mail do exame admissional ao colaborador' : 'Orientações do exame ao colaborador'}
                </h5>
                <small style={{ color: '#6c757d' }}>
                  {colaborador.nome} · {adm ? 'data, horário, clínica, endereço e a guia do exame' : 'e-mail com clínica, endereço, horário e o kit'}
                </small>
              </div>
              <button type="button" className="btn-close" onClick={onFechar}></button>
            </div>
            <div className="modal-body">
              {!form ? (
                <div className="text-center text-muted py-4"><i className="fas fa-spinner fa-spin me-2"></i>Carregando…</div>
              ) : (
                <>
                  <div className="row g-2">
                    <div className="col-md-6">
                      <label className="form-label small fw-semibold">Para</label>
                      <input className={`form-control form-control-sm ${!form.para ? 'border-danger' : ''}`}
                        value={form.para} onChange={(e) => mudar('para', e.target.value)} placeholder="e-mail do colaborador" />
                      {!info?.para && (
                        <div className="small text-danger mt-1">
                          <i className="fas fa-triangle-exclamation me-1"></i>Sem e-mail na Convenia — digite o endereço.
                        </div>
                      )}
                      {info?.para && ORIGEM[info.origem_email] && (
                        <div className="small text-warning-emphasis mt-1">
                          <i className="fas fa-circle-info me-1"></i>Sem e-mail pessoal na Convenia — usando o {ORIGEM[info.origem_email]}.
                        </div>
                      )}
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small fw-semibold">{adm ? 'Data do exame' : 'Data do atendimento'}</label>
                      <input type="date" className={`form-control form-control-sm ${!form.data ? 'border-warning' : ''}`}
                        value={form.data} onChange={(e) => mudarData(e.target.value)} />
                      {adm && info?.data_origem && form.data === info.data_iso && (
                        <div className="small text-muted mt-1">da {info.data_origem}</div>
                      )}
                    </div>
                    <div className="col-md-3">
                      <label className="form-label small fw-semibold">Enviar como</label>
                      <select className="form-select form-select-sm" value={form.operador_id}
                        onChange={(e) => mudar('operador_id', e.target.value)}>
                        <option value="">Escolha…</option>
                        {(info?.remetentes || []).map(r => <option key={r.id} value={String(r.id)}>{r.nome}</option>)}
                      </select>
                    </div>
                    {adm && CAMPOS_ADM.map(([campo, rotulo, dica]) => (
                      <div key={campo} className={campo === 'endereco' ? 'col-md-6' : 'col-md-3'}>
                        <label className="form-label small fw-semibold">{rotulo}</label>
                        <input className={`form-control form-control-sm ${!form[campo] ? 'border-warning' : ''}`}
                          value={form[campo]} placeholder={dica}
                          onChange={(e) => mudarCampoAdm(campo, e.target.value)} />
                      </div>
                    ))}
                    {adm && (
                      <div className="col-12 small text-muted">
                        <i className="fas fa-circle-info me-1"></i>
                        A data vem do pedido à clínica; clínica e endereço, do último envio desta empresa. Ajuste se precisar —
                        o texto abaixo acompanha os campos.
                      </div>
                    )}
                    <div className="col-12">
                      <label className="form-label small fw-semibold">Assunto</label>
                      <input className="form-control form-control-sm" value={form.assunto}
                        onChange={(e) => editarTexto('assunto', e.target.value)} />
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-semibold d-flex align-items-center">
                        Texto
                        <span className="ms-auto small fw-normal text-muted">
                          {info?.modelo_proprio ? 'Modelo desta empresa' : 'Modelo padrão'}
                          {info?.modelo_proprio && (
                            <button type="button" className="btn btn-link btn-sm p-0 ms-2" onClick={restaurar}>restaurar padrão</button>
                          )}
                        </span>
                      </label>
                      <textarea className="form-control form-control-sm" rows={16} value={form.corpo}
                        onChange={(e) => editarTexto('corpo', e.target.value)} style={{ fontSize: '.85rem' }} />
                      <div className="form-text">
                        Linha que começa com 📌 📄 ✅ 🩺 vira título; "Rótulo: valor" fica com o rótulo em negrito.
                        Use "Salvar como modelo" para guardar o texto desta empresa (ele também é salvo ao enviar);
                        {adm ? ' o nome, a data, o horário, a clínica e o endereço' : ' o nome e a data'} se ajustam sozinhos para cada colaborador.
                      </div>
                    </div>
                    <div className="col-12">
                      <label className="form-label small fw-semibold">{adm ? 'Anexos' : 'Anexos (kit do exame)'}</label>
                      {adm && (
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <div className="form-check m-0">
                            <input className="form-check-input" type="checkbox" id="anexarGuiaAdm"
                              checked={form.anexar_guia} onChange={(e) => mudar('anexar_guia', e.target.checked)} />
                            <label className="form-check-label small" htmlFor="anexarGuiaAdm">
                              Anexar a guia do exame (PDF gerado, igual ao enviado à clínica)
                            </label>
                          </div>
                          <button type="button" className="btn btn-link btn-sm p-0" onClick={verGuia}>ver guia</button>
                        </div>
                      )}
                      <input type="file" multiple className="form-control form-control-sm"
                        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                        onChange={(e) => { juntarAnexos(e.target.files); e.target.value = ''; }} />
                      {form.anexos.length > 0 && (
                        <ul className="list-unstyled small mt-2 mb-0">
                          {form.anexos.map((a) => (
                            <li key={a.name}>
                              <i className="fas fa-paperclip me-1 text-muted"></i>{a.name}
                              <button type="button" className="btn btn-link btn-sm text-danger p-0 ms-2"
                                onClick={() => mudar('anexos', form.anexos.filter(x => x !== a))}>remover</button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                  {(info?.historico || []).length > 0 && (
                    <div className="small text-muted mt-3">
                      <b>Já enviado:</b>{' '}
                      {info.historico.map(h => `${h.em}${h.data_atendimento ? ` (${adm ? 'exame' : 'atendimento'} ${h.data_atendimento})` : ''} por ${h.operador || '—'}`).join(' · ')}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer bg-light">
              <button className="btn btn-secondary" onClick={onFechar}><i className="fas fa-times me-2"></i>Fechar</button>
              <button className="btn btn-outline-primary" onClick={salvarModelo} disabled={!form || ocupado}
                title="Guarda este assunto e texto como modelo da empresa (os dados da pessoa se ajustam para cada colaborador)">
                <i className="fas fa-floppy-disk me-2"></i>Salvar como modelo
              </button>
              <button className="btn btn-primary" onClick={verPrevia} disabled={!form || ocupado}>
                <i className={`fas ${ocupado ? 'fa-spinner fa-spin' : 'fa-eye'} me-2`}></i>Ver prévia
              </button>
            </div>
          </div>
        </div>
      </div>

      {previa && (
        <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10001 }}>
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-eye me-2"></i>Prévia do e-mail
                  </h5>
                  <small style={{ color: '#6c757d' }}>{colaborador.nome} · é assim que o colaborador vai receber</small>
                </div>
                <button type="button" className="btn-close" onClick={() => setPrevia(null)}></button>
              </div>
              <div className="modal-body p-0">
                <div className="px-3 py-2 small border-bottom" style={{ background: '#f8f9fa' }}>
                  <div><span className="text-muted" style={{ display: 'inline-block', width: 70 }}>De:</span> {previa.de}</div>
                  <div><span className="text-muted" style={{ display: 'inline-block', width: 70 }}>Para:</span> {previa.para}</div>
                  {previa.cc && <div><span className="text-muted" style={{ display: 'inline-block', width: 70 }}>Cc:</span> {previa.cc}</div>}
                  {previa.bcc && <div><span className="text-muted" style={{ display: 'inline-block', width: 70 }}>Cco:</span> {previa.bcc}</div>}
                  <div><span className="text-muted" style={{ display: 'inline-block', width: 70 }}>Assunto:</span> <b>{previa.assunto}</b></div>
                  <div>
                    <span className="text-muted" style={{ display: 'inline-block', width: 70 }}>Anexos:</span>
                    {(previa.anexos || []).length ? previa.anexos.join(', ') : <span className="text-warning-emphasis">nenhum</span>}
                  </div>
                  {(previa.avisos || []).map((a, i) => (
                    <div key={i} className="text-warning-emphasis mt-1"><i className="fas fa-triangle-exclamation me-1"></i>{a}</div>
                  ))}
                </div>
                <iframe title="E-mail" srcDoc={previa.html} sandbox=""
                  style={{ width: '100%', height: 560, border: 0, background: '#f4f6fa' }} />
              </div>
              <div className="modal-footer bg-light">
                <button className="btn btn-secondary" onClick={() => setPrevia(null)}>
                  <i className="fas fa-pen me-2"></i>Voltar e editar
                </button>
                <button className="btn btn-success" onClick={enviar} disabled={ocupado || bloqueiaEnvio}
                  title={bloqueiaEnvio ? 'Resolva os avisos em amarelo' : 'Enviar ao colaborador'}>
                  <i className={`fas ${ocupado ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-2`}></i>Enviar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AvisoExameColaborador;
